import { type RefObject, useLayoutEffect, useState } from "react";

import { CIRCLE_SIZE } from "./creditProgressBarConfig";

interface UseTooltipPositionParams {
  containerRef: RefObject<HTMLDivElement | null>;
  currentProgress: number;
  showTooltip: boolean;
}

export function useTooltipPosition(params: UseTooltipPositionParams): number {
  const { containerRef, currentProgress, showTooltip } = params;
  const [tooltipLeftPx, setTooltipLeftPx] = useState(0);

  useLayoutEffect(() => {
    if (!showTooltip || !containerRef.current) {
      return;
    }

    let rafId: number | null = null;

    const calculatePosition = (): void => {
      if (!containerRef.current) {
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth === 0) {
        return;
      }

      // The visible progress bar area is the container minus the full circle width.
      const visibleProgressWidth = Math.max(0, containerWidth - CIRCLE_SIZE);
      setTooltipLeftPx(visibleProgressWidth * currentProgress);
    };

    const schedulePositionUpdate = (): void => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(calculatePosition);
    };

    schedulePositionUpdate();

    const resizeObserver = new ResizeObserver(() => {
      schedulePositionUpdate();
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      resizeObserver.disconnect();
    };
  }, [containerRef, currentProgress, showTooltip]);

  return tooltipLeftPx;
}
